// ANCHOR React
import { memo, useEffect } from 'react';

// ANCHOR Next
import Head from 'next/head';
import { useRouter } from 'next/router';
import Image from 'next/image';

// ANCHOR Hooks
import { useSentryExceptionHandler } from '@lyon/utils/hooks/useSentryExceptionHandler';

// ANCHOR Base
import { styled } from 'baseui';
import { StyledLink } from 'baseui/link';
import { Block } from 'baseui/block';
import { LabelMedium } from 'baseui/typography';

// ANCHOR Constants
import { IMG_ALT, IMG_SOURCE } from './constants';

// ANCHOR Styles
import { BLOCK, LOGO, INNER_BLOCK } from './styles';

const LyonLogo = styled(Image, LOGO);

interface Props {
  href: string;
  code?: number;
}

export const Redirect = memo(({ href, code }: Props) => {
  const router = useRouter();
  const catchException = useSentryExceptionHandler();

  useEffect(() => {
    if (href[0] === '/') {
      router.push(href, '', {
        shallow: true,
      }).catch(catchException);
    } else {
      window.location.href = href;
    }
  }, [href, code, router, catchException]);

  return (
    <>
      <Head>
        {/* Redirects with meta refresh if no JavaScript support */}
        <noscript>
          <meta httpEquiv="refresh" content={`0;url=${href}`} />
        </noscript>
        {(code === undefined || code === 301) && (
          <link rel="canonical" href={href} />
        )}
      </Head>
      <Block overrides={BLOCK}>
        <Block overrides={INNER_BLOCK}>
          <Block
            marginLeft="32px"
            marginRight="32px"
            marginTop="32px"
            marginBottom="32px"
          >
            <LyonLogo
              width="64"
              height="64"
              layout="fixed"
              src={IMG_SOURCE}
              alt={IMG_ALT}
            />
          </Block>
          <LabelMedium>
            Redirecting to
            <StyledLink href={href} style={{ marginLeft: '4px' }}>
              {href}
            </StyledLink>
            &hellip;
          </LabelMedium>
        </Block>
      </Block>
    </>
  );
});
